<section class="max-w-4xl mx-auto px-4 py-10 text-slate-100">
  <header class="mb-8">
    <h1 class="text-3xl md:text-4xl font-extrabold mb-3">
      Destiny 2 Activity History Tracker
    </h1>
    <p class="text-slate-300 text-lg">
      Destiny Chronicle is a Destiny 1 and Destiny 2 activity history tracker that turns your Bungie PGCR data into a searchable journal of every raid, dungeon, and match you have played.
    </p>
  </header>

  <section class="mb-8">
    <h2 class="text-2xl font-semibold mb-2">What is Destiny activity history?</h2>
    <p class="text-slate-300 mb-3">
      Destiny activity history is the complete record of your fireteam's Post Game Carnage Reports (PGCRs) — every strike, raid, dungeon, Crucible match, and playlist run. Destiny Chronicle pulls this data from the official Bungie API and lets you browse it by date so you can answer questions like:
    </p>
    <ul class="list-disc list-inside text-slate-300 space-y-1">
      <li>"What was I doing in Destiny on this date years ago?"</li>
      <li>"When was my first clear of each raid or dungeon?"</li>
      <li>"How much time have I really spent in Destiny activities?"</li>
    </ul>
  </section>

  <section class="mb-8">
    <h2 class="text-2xl font-semibold mb-2">Destiny Chronicle vs other Destiny tools</h2>
    <p class="text-slate-300 mb-3">
      Destiny Chronicle is designed to complement, not replace, existing Destiny tools you might already know:
    </p>
    <ul class="list-disc list-inside text-slate-300 space-y-1">
      <li><strong>Destiny Heatmap</strong> and <strong>Scrubland / Scrublanddeux</strong> focus on calendar style playtime heatmaps.</li>
      <li><strong>Time Wasted on Destiny</strong> is great for a quick "how much time have I spent" snapshot.</li>
      <li><strong>Chrisfried&apos;s tools</strong> and other community sites offer powerful visualizations.</li>
    </ul>
    <p class="text-slate-300 mt-3">
      Destiny Chronicle concentrates on <strong>daily PGCR history</strong> and <strong>Guardian Firsts</strong> — a detailed journal of activities across Destiny 1 and Destiny 2, organized by date, year and mode with raid and dungeon firsts highlighted.
    </p>
  </section>

  <section class="mb-8">
    <h2 class="text-2xl font-semibold mb-2">Features of the Destiny activity history view</h2>
    <ul class="list-disc list-inside text-slate-300 space-y-1">
      <li>Browse your Destiny 2 activity history by specific calendar date across multiple years.</li>
      <li>See Destiny 1 and Destiny 2 activities side by side, grouped by raid, dungeon, and playlist.</li>
      <li>View <strong>Guardian Firsts</strong> such as first ever activity, first raid clears, and first dungeon solos.</li>
      <li>Export activity history, firsts, and titles to CSV/Excel for personal archives.</li>
    </ul>
  </section>

  <section class="mb-8">
    <h2 class="text-2xl font-semibold mb-2">Frequently asked questions</h2>
    <h3 class="text-xl font-semibold mt-4 mb-1">How do I see my Destiny 2 activity history?</h3>
    <p class="text-slate-300">
      Open Destiny Chronicle, search for your Bungie name or membership ID, choose your platform, and select a date. The app will fetch your Bungie PGCR activity history for all linked characters and show everything you played on that day across Destiny 1 and Destiny 2.
    </p>

    <h3 class="text-xl font-semibold mt-4 mb-1">Does Destiny Chronicle support Destiny 1?</h3>
    <p class="text-slate-300">
      Yes. Destiny Chronicle was built to preserve <strong>Destiny 1 activity history</strong> as well as Destiny 2. D1 raids and activities appear alongside D2 content for the same calendar date.
    </p>

    <h3 class="text-xl font-semibold mt-4 mb-1">Is this affiliated with Time Wasted on Destiny or Destiny Heatmap?</h3>
    <p class="text-slate-300">
      No. Destiny Chronicle is a standalone fan project built on the Bungie API. It is inspired by community tools like <strong>Time Wasted on Destiny</strong>, <strong>Destiny Heatmap</strong>, and <strong>Scrublanddeux</strong>, but focuses on detailed per-date activity history and Guardian Firsts.
    </p>
  </section>
</section>

